import { API_BASE_URL } from './channelService';
import { userService } from './userService';

export interface ShopifyPriceRule {
  id: string | number;
  title: string;
  value_type?: 'fixed_amount' | 'percentage';
  value?: string;
  customer_selection?: 'all' | 'prerequisite';
  target_type?: 'line_item' | 'shipping_line';
  target_selection?: 'all' | 'entitled';
  allocation_method?: 'across' | 'each';
  usage_limit?: number | null;
  once_per_customer?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  created_at?: string;
  updated_at?: string;
  entitled_product_ids?: number[];
  entitled_variant_ids?: number[];
  entitled_collection_ids?: number[];
  entitled_country_ids?: number[];
  prerequisite_subtotal_range?: {
    greater_than_or_equal_to?: string;
  };
  prerequisite_quantity_range?: {
    greater_than_or_equal_to?: number;
  };
  prerequisite_shipping_price_range?: {
    less_than_or_equal_to?: string;
  };
  prerequisite_to_entitlement_quantity_ratio?: {
    prerequisite_quantity?: number;
    entitled_quantity?: number;
  };
}

export interface ShopifyDiscountCode {
  id: string | number;
  code: string;
}

async function authHeaders() {
  const jwtToken = await userService.getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${jwtToken}`,
  } as Record<string, string>;
}

// Base path matches API Gateway resources: /shopify/shopify-actions
const ACTIONS_BASE = `${API_BASE_URL}/shopify/shopify-actions`;

export async function listPriceRules(channelId: string, params: { limit?: number; page_info?: string } = {}) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (channelId) qs.set('channel_id', channelId);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.page_info) qs.set('page_info', params.page_info);
  const url = `${ACTIONS_BASE}/price_rules${qs.toString() ? `?${qs.toString()}` : ''}`;
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) throw new Error(`Failed to list price rules: ${resp.status}`);
  const json = await resp.json();
  const rules = (json.price_rules || json.priceRules || json.items || []) as any[];
  return rules as ShopifyPriceRule[];
}

export async function listDiscountCodes(channelId: string, priceRuleId: string, params: { limit?: number; page_info?: string } = {}) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (channelId) qs.set('channel_id', channelId);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.page_info) qs.set('page_info', params.page_info);
  const url = `${ACTIONS_BASE}/price_rules/${encodeURIComponent(priceRuleId)}/discount_codes${qs.toString() ? `?${qs.toString()}` : ''}`;
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) throw new Error(`Failed to list discount codes: ${resp.status}`);
  const json = await resp.json();
  const codes = (json.discount_codes || json.items || []) as any[];
  return codes as ShopifyDiscountCode[];
}

export async function createDiscountCode(channelId: string, priceRuleId: string, code: string) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (channelId) qs.set('channel_id', channelId);
  const url = `${ACTIONS_BASE}/price_rules/${encodeURIComponent(priceRuleId)}/discount_codes${qs.toString() ? `?${qs.toString()}` : ''}`;
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ code }) });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to create discount code: ${resp.status} - ${text}`);
  }
  return await resp.json();
}

export async function createCreatorCode(body: { creator_id: string; price_rule_id: string; code: string; channel_id: string; project_uuid?: string }) {
  const headers = await authHeaders();
  const url = `${ACTIONS_BASE}/creator_codes`;
  const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to create creator code: ${resp.status} - ${text}`);
  }
  return await resp.json();
}

export async function deleteCreatorCode(body: { creator_id: string; code: string; channel_id: string; project_uuid?: string; price_rule_id?: string }) {
  const headers = await authHeaders();
  const url = `${ACTIONS_BASE}/creator_codes`;
  const resp = await fetch(url, { method: 'DELETE', headers, body: JSON.stringify(body) });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to delete creator code: ${resp.status} - ${text}`);
  }
  return await resp.json();
}

export async function getDiscountCodeAnalytics(params: { code: string; prompt?: string; channel_id: string; project_uuid?: string }) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  qs.set('code', params.code);
  if (params.prompt) {
    qs.set('prompt', params.prompt);
  }
  qs.set('channel_id', params.channel_id);
  if (params.project_uuid) qs.set('project_uuid', params.project_uuid);
  
  const url = `${API_BASE_URL}/shopify/discount-code-analytics?${qs.toString()}`;
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to get discount code analytics: ${resp.status} - ${text}`);
  }
  return await resp.json();
}

export const shopifyActionsService = {
  listPriceRules,
  listDiscountCodes,
  createDiscountCode,
  createCreatorCode,
  deleteCreatorCode,
  getDiscountCodeAnalytics,
};


